import React, { useState, useEffect } from 'react';

const SMSFAssetAllocationModal = ({ 
    isOpen, 
    onClose, 
    members = [], 
    assetBreakdown = {}, 
    onApplyAllocation 
}) => {
    const [allocationMode, setAllocationMode] = useState('equal'); // 'equal', 'percentage', 'manual'
    const [percentageAllocations, setPercentageAllocations] = useState({});
    const [manualAllocations, setManualAllocations] = useState({});
    const [previewData, setPreviewData] = useState([]);

    // Initialize percentage allocations (equal split by default)
    useEffect(() => {
        if (members.length > 0) {
            const equalPct = 100 / members.length;
            const initial = {};
            members.forEach(member => {
                initial[member.name] = equalPct;
            });
            setPercentageAllocations(initial);
        }
    }, [members]);

    // Calculate total assets
    const totalProductiveAssets = (assetBreakdown.shares || 0) + 
                                  (assetBreakdown.infrastructure || 0) + 
                                  (assetBreakdown.cash || 0);
    
    const totalNonProductiveAssets = (assetBreakdown.commercialProperty || 0) + 
                                      (assetBreakdown.residentialProperty || 0) + 
                                      (assetBreakdown.collectibles || 0);
    
    const totalAssets = totalProductiveAssets + totalNonProductiveAssets;

    // Calculate preview based on current mode
    useEffect(() => {
        if (allocationMode === 'equal') {
            calculateEqualSplit();
        } else if (allocationMode === 'percentage') {
            calculatePercentageSplit();
        }
    }, [allocationMode, percentageAllocations, members, assetBreakdown]);

    const calculateEqualSplit = () => {
        const split = 1 / members.length;
        const preview = members.map(member => ({
            name: member.name,
            percentage: (split * 100).toFixed(2),
            total: Math.round(totalAssets * split),
            productiveAssets: {
                shares: Math.round((assetBreakdown.shares || 0) * split),
                infrastructure: Math.round((assetBreakdown.infrastructure || 0) * split),
                cash: Math.round((assetBreakdown.cash || 0) * split),
                total: Math.round(totalProductiveAssets * split)
            },
            nonProductiveAssets: {
                commercialProperty: Math.round((assetBreakdown.commercialProperty || 0) * split),
                residentialProperty: Math.round((assetBreakdown.residentialProperty || 0) * split),
                collectibles: Math.round((assetBreakdown.collectibles || 0) * split),
                total: Math.round(totalNonProductiveAssets * split)
            }
        }));
        setPreviewData(preview);
    };

    const calculatePercentageSplit = () => {
        const preview = members.map(member => {
            const pct = (percentageAllocations[member.name] || 0) / 100;
            return {
                name: member.name,
                percentage: (pct * 100).toFixed(2),
                total: Math.round(totalAssets * pct),
                productiveAssets: {
                    shares: Math.round((assetBreakdown.shares || 0) * pct),
                    infrastructure: Math.round((assetBreakdown.infrastructure || 0) * pct),
                    cash: Math.round((assetBreakdown.cash || 0) * pct),
                    total: Math.round(totalProductiveAssets * pct)
                },
                nonProductiveAssets: {
                    commercialProperty: Math.round((assetBreakdown.commercialProperty || 0) * pct),
                    residentialProperty: Math.round((assetBreakdown.residentialProperty || 0) * pct),
                    collectibles: Math.round((assetBreakdown.collectibles || 0) * pct),
                    total: Math.round(totalNonProductiveAssets * pct)
                }
            };
        });
        setPreviewData(preview);
    };

    const handlePercentageChange = (memberName, value) => {
        const numValue = parseFloat(value) || 0;
        setPercentageAllocations(prev => ({
            ...prev,
            [memberName]: numValue
        }));
    };

    const getTotalPercentage = () => {
        return Object.values(percentageAllocations).reduce((sum, val) => sum + val, 0);
    };

    const isValidAllocation = () => {
        if (allocationMode === 'equal') return true;
        if (allocationMode === 'percentage') {
            const total = getTotalPercentage();
            return Math.abs(total - 100) < 0.01; // Allow tiny rounding errors
        }
        return true;
    };

    const handleApply = () => {
        if (!isValidAllocation()) {
            alert('⚠️ Invalid Allocation\n\nPercentages must sum to 100%');
            return;
        }

        // Apply the allocation
        onApplyAllocation(previewData, allocationMode);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-2xl font-bold">📊 Allocate Assets to Members</h2>
                            <p className="text-sm text-blue-100 mt-1">
                                Choose how to split ${totalAssets.toLocaleString()} among {members.length} members
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                <div className="p-6">
                    {/* Mode Selector */}
                    <div className="mb-6">
                        <div className="flex gap-4 border-b border-gray-200">
                            <button
                                onClick={() => setAllocationMode('equal')}
                                className={`px-6 py-3 font-medium transition ${
                                    allocationMode === 'equal'
                                        ? 'border-b-2 border-blue-600 text-blue-600'
                                        : 'text-gray-600 hover:text-blue-600'
                                }`}
                            >
                                Equal Split
                            </button>
                            <button
                                onClick={() => setAllocationMode('percentage')}
                                className={`px-6 py-3 font-medium transition ${
                                    allocationMode === 'percentage'
                                        ? 'border-b-2 border-blue-600 text-blue-600'
                                        : 'text-gray-600 hover:text-blue-600'
                                }`}
                            >
                                Percentage Split
                            </button>
                            <button
                                onClick={() => setAllocationMode('manual')}
                                className={`px-6 py-3 font-medium transition ${
                                    allocationMode === 'manual'
                                        ? 'border-b-2 border-blue-600 text-blue-600'
                                        : 'text-gray-600 hover:text-gray-400'
                                } opacity-50 cursor-not-allowed`}
                                disabled
                            >
                                Manual Split <span className="text-xs">(Coming Soon)</span>
                            </button>
                        </div>
                    </div>

                    {/* Asset Summary */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <h3 className="font-semibold text-blue-900 mb-3">Fund Assets Summary</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <div className="text-gray-600">📈 Shares</div>
                                <div className="font-semibold text-gray-900">${(assetBreakdown.shares || 0).toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="text-gray-600">⚡ Infrastructure</div>
                                <div className="font-semibold text-gray-900">${(assetBreakdown.infrastructure || 0).toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="text-gray-600">💰 Cash</div>
                                <div className="font-semibold text-gray-900">${(assetBreakdown.cash || 0).toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="text-gray-600">🏢 Property</div>
                                <div className="font-semibold text-gray-900">${((assetBreakdown.commercialProperty || 0) + (assetBreakdown.residentialProperty || 0)).toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="text-gray-600">🎨 Collectibles</div>
                                <div className="font-semibold text-gray-900">${(assetBreakdown.collectibles || 0).toLocaleString()}</div>
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <div className="text-gray-600">💼 Productive Total</div>
                                <div className="font-semibold text-blue-700">${totalProductiveAssets.toLocaleString()}</div>
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <div className="text-gray-600">📦 Non-Productive Total</div>
                                <div className="font-semibold text-purple-700">${totalNonProductiveAssets.toLocaleString()}</div>
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <div className="text-gray-600">💎 Fund Total</div>
                                <div className="font-bold text-green-700 text-lg">${totalAssets.toLocaleString()}</div>
                            </div>
                        </div>
                    </div>

                    {/* Mode-Specific Content */}
                    {allocationMode === 'equal' && (
                        <div className="mb-6">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                                <p className="text-sm text-green-800">
                                    <strong>ℹ️ Equal Split Mode:</strong> All assets will be divided equally among {members.length} members ({(100 / members.length).toFixed(2)}% each).
                                </p>
                            </div>
                        </div>
                    )}

                    {allocationMode === 'percentage' && (
                        <div className="mb-6">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                <p className="text-sm text-blue-800 mb-3">
                                    <strong>ℹ️ Percentage Split Mode:</strong> Set custom percentages for each member. Percentages must sum to 100%.
                                </p>
                                <div className="space-y-3">
                                    {members.map(member => (
                                        <div key={member.name} className="flex items-center gap-4">
                                            <label className="w-48 font-medium text-gray-700">{member.name}:</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                value={percentageAllocations[member.name] || 0}
                                                onChange={(e) => handlePercentageChange(member.name, e.target.value)}
                                                className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <span className="text-gray-600">%</span>
                                            <span className="text-gray-500 text-sm">
                                                = ${Math.round(totalAssets * (percentageAllocations[member.name] || 0) / 100).toLocaleString()}
                                            </span>
                                        </div>
                                    ))}
                                    <div className="pt-3 border-t border-blue-200 flex items-center gap-4">
                                        <span className="w-48 font-bold text-gray-900">Total:</span>
                                        <span className={`font-bold ${Math.abs(getTotalPercentage() - 100) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                                            {getTotalPercentage().toFixed(2)}%
                                        </span>
                                        {Math.abs(getTotalPercentage() - 100) < 0.01 ? (
                                            <span className="text-green-600 text-sm">✓ Valid</span>
                                        ) : (
                                            <span className="text-red-600 text-sm">⚠️ Must equal 100%</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Preview */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Allocation Preview</h3>
                        <div className="space-y-4">
                            {previewData.map((member, index) => (
                                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    <div className="flex justify-between items-start mb-3">
                                        <h4 className="font-semibold text-gray-900 text-lg">{member.name}</h4>
                                        <div className="text-right">
                                            <div className="text-sm text-gray-600">{member.percentage}%</div>
                                            <div className="text-lg font-bold text-blue-600">${member.total.toLocaleString()}</div>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        {/* Productive Assets */}
                                        <div className="bg-white rounded-lg p-3 border border-blue-100">
                                            <div className="font-medium text-blue-900 mb-2">💼 Productive Assets (${member.productiveAssets.total.toLocaleString()})</div>
                                            <div className="space-y-1 text-gray-700">
                                                <div className="flex justify-between">
                                                    <span>📈 Shares:</span>
                                                    <span className="font-medium">${member.productiveAssets.shares.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>⚡ Infrastructure:</span>
                                                    <span className="font-medium">${member.productiveAssets.infrastructure.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span>💰 Cash:</span>
                                                    <span className="font-medium">${member.productiveAssets.cash.toLocaleString()}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Non-Productive Assets */}
                                        <div className="bg-white rounded-lg p-3 border border-purple-100">
                                            <div className="font-medium text-purple-900 mb-2">📦 Non-Productive Assets (${member.nonProductiveAssets.total.toLocaleString()})</div>
                                            <div className="space-y-1 text-gray-700">
                                                {member.nonProductiveAssets.commercialProperty > 0 && (
                                                    <div className="flex justify-between">
                                                        <span>🏢 Commercial Property:</span>
                                                        <span className="font-medium">${member.nonProductiveAssets.commercialProperty.toLocaleString()}</span>
                                                    </div>
                                                )}
                                                {member.nonProductiveAssets.residentialProperty > 0 && (
                                                    <div className="flex justify-between">
                                                        <span>🏘️ Residential Property:</span>
                                                        <span className="font-medium">${member.nonProductiveAssets.residentialProperty.toLocaleString()}</span>
                                                    </div>
                                                )}
                                                {member.nonProductiveAssets.collectibles > 0 && (
                                                    <div className="flex justify-between">
                                                        <span>🎨 Collectibles:</span>
                                                        <span className="font-medium">${member.nonProductiveAssets.collectibles.toLocaleString()}</span>
                                                    </div>
                                                )}
                                                {member.nonProductiveAssets.total === 0 && (
                                                    <div className="text-gray-400 italic">None</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApply}
                            disabled={!isValidAllocation()}
                            className={`px-6 py-3 rounded-lg font-medium transition ${
                                isValidAllocation()
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            Apply Allocation
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SMSFAssetAllocationModal;



















